import { useCompleteTask } from '@/api/system/tasks/completeTask';
import { useCreateTask } from '@/api/system/tasks/createTask';
import { useDeleteTask } from '@/api/system/tasks/deleteTask';
import { useTask } from '@/api/system/tasks/getTask';
import { useUncompleteTask } from '@/api/system/tasks/uncompleteTask';
import { useUpdateTask } from '@/api/system/tasks/updateTask';
import { DayPicker } from '@/components/DatePicker/DayPicker';
import { ConfirmationDialog } from '@/components/Elements';
import { Button } from '@/components/ui/elements/button';
import { Checkbox } from '@/components/ui/elements/checkbox';
import { Input } from '@/components/ui/elements/input';
import { Textarea } from '@/components/ui/elements/textarea';
import { Task, WorkOrder } from '@/types';
import {
  calendarDateToDate,
  cn,
  dateToCalendarDate,
  formatDate,
  stringToCalendarDate,
} from '@/utils/format';
import { TrashIcon } from '@heroicons/react/24/outline';
import { FocusEvent, KeyboardEvent, useMemo, useState } from 'react';

interface WorkOrderSubtasksSectionProps {
  workOrder: WorkOrder;
  canEdit: boolean;
}

const normalizeDateInputValue = (date: string | null | undefined) => {
  if (!date) return '';

  return date.split(' ')[0].split('T')[0];
};

const toPickerDate = (date: string | null | undefined) => {
  const calendarDate = stringToCalendarDate(normalizeDateInputValue(date) || null);

  return calendarDate ? calendarDateToDate(calendarDate) : null;
};

const toApiDate = (date: Date) => {
  return `${dateToCalendarDate(date).year.toString().padStart(4, '0')}-${dateToCalendarDate(date)
    .month.toString()
    .padStart(2, '0')}-${dateToCalendarDate(date).day.toString().padStart(2, '0')}`;
};

const getSubtaskNameRows = (value: string) => {
  const estimatedRows = value.split('\n').reduce((rows, line) => {
    return rows + Math.max(1, Math.ceil(Math.max(line.length, 1) / 48));
  }, 0);

  return Math.max(2, estimatedRows);
};

export const WorkOrderSubtasksSection = ({ workOrder, canEdit }: WorkOrderSubtasksSectionProps) => {
  const [newSubtaskName, setNewSubtaskName] = useState('');
  const [newSubtaskDueAt, setNewSubtaskDueAt] = useState('');

  const { data } = useTask({
    id: workOrder.task_id,
    config: {
      initialData: workOrder,
      enabled: !!workOrder.task_id,
    },
  });

  const subtasks = useMemo(
    () => data?.subtasks ?? workOrder.subtasks ?? [],
    [data?.subtasks, workOrder.subtasks],
  );

  const { mutate: createTask, isPending: isCreatingSubtask } = useCreateTask({
    onSuccess: () => {
      setNewSubtaskName('');
      setNewSubtaskDueAt('');
    },
  });

  const { mutate: updateTask } = useUpdateTask({});

  const { mutate: completeTask } = useCompleteTask({
    onMutate: () => undefined,
  });

  const { mutate: uncompleteTask } = useUncompleteTask({
    onMutate: () => undefined,
  });

  const { mutate: deleteTask } = useDeleteTask({});

  const trimmedSubtaskName = newSubtaskName.trim();
  const completedSubtasks = subtasks.filter((subtask) => subtask.completed).length;

  const handleCreateSubtask = () => {
    if (!canEdit || !trimmedSubtaskName) return;

    createTask({
      data: {
        franchise_id: workOrder.franchise_id,
        type: workOrder.type,
        name: trimmedSubtaskName,
        description: null,
        parent_task_id: workOrder.task_id,
        taskable_id: workOrder.taskable_id,
        taskable_type: workOrder.taskable_type,
        due_at: newSubtaskDueAt || null,
        meta: {
          status: 'Reported',
        },
      },
    });
  };

  const handleSubtaskKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    handleCreateSubtask();
  };

  const handleSubtaskNameBlur = (subtask: Task, event: FocusEvent<HTMLTextAreaElement>) => {
    if (!canEdit) return;

    const nextName = event.currentTarget.value.trim();

    if (!nextName || nextName === subtask.name) return;

    updateTask({
      id: subtask.task_id,
      data: {
        name: nextName,
      },
    });
  };

  const handleSubtaskDueAtChange = (subtask: Task, nextDueAt: Date) => {
    if (!canEdit) return;

    const currentDueAt = normalizeDateInputValue(subtask.due_at);
    const normalizedNextDueAt = toApiDate(nextDueAt);

    if (currentDueAt === normalizedNextDueAt) return;

    updateTask({
      id: subtask.task_id,
      data: {
        due_at: normalizedNextDueAt || null,
      },
    });
  };

  const clearSubtaskDueAt = (subtask: Task) => {
    if (!canEdit || !subtask.due_at) return;

    updateTask({
      id: subtask.task_id,
      data: {
        due_at: null,
      },
    });
  };

  const toggleSubtask = (subtask: Task) => {
    if (!canEdit) return;

    if (subtask.completed) {
      uncompleteTask({ task: subtask });
      return;
    }

    completeTask({ task: subtask });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          {completedSubtasks}/{subtasks.length} complete
        </span>
        {subtasks.length > 0 && <span>{subtasks.length} total</span>}
      </div>

      {subtasks.length > 0 ? (
        <div className="space-y-2">
          {subtasks.map((subtask) => (
            <div
              key={subtask.task_id}
              className="flex flex-col gap-2 rounded-md border bg-muted/20 px-2.5 py-2"
            >
              <div>
                {canEdit ? (
                  <Textarea
                    key={`subtask-name-${subtask.task_id}-${subtask.name}`}
                    defaultValue={subtask.name}
                    rows={getSubtaskNameRows(subtask.name)}
                    aria-label={`Subtask name ${subtask.name}`}
                    onBlur={(event) => handleSubtaskNameBlur(subtask, event)}
                    className={cn(
                      'min-h-0 resize-none border-transparent bg-transparent shadow-none text-sm leading-snug focus:border-foreground/30 focus:bg-background focus:shadow-xs',
                      subtask.completed && 'text-muted-foreground line-through',
                    )}
                  />
                ) : (
                  <p
                    className={cn(
                      'text-sm leading-snug whitespace-pre-wrap wrap-break-word',
                      subtask.completed && 'text-muted-foreground line-through',
                    )}
                  >
                    {subtask.name}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={subtask.completed}
                  disabled={!canEdit}
                  aria-label={`Toggle subtask ${subtask.name}`}
                  onClick={() => toggleSubtask(subtask)}
                />
                <div className="flex-1" />
                <div className="flex flex-wrap items-center gap-2">
                  {canEdit ? (
                    <DayPicker
                      setDate={(date) => handleSubtaskDueAtChange(subtask, date)}
                      onClear={() => clearSubtaskDueAt(subtask)}
                      date={toPickerDate(subtask.due_at)}
                      mode="button"
                      isRequired={false}
                    />
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {subtask.due_at ? formatDate(subtask.due_at, 'MM/dd/yyyy') : 'Not scheduled'}
                    </span>
                  )}

                  {canEdit && (
                    <ConfirmationDialog
                      triggerButton={
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Delete subtask ${subtask.name}`}
                        >
                          <TrashIcon className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      }
                      confirmButtonText="Delete"
                      confirmAction={() => deleteTask({ id: subtask.id, data: subtask })}
                      title={`Delete subtask "${subtask.name}"?`}
                      body="This action cannot be undone."
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-md border border-dashed px-3 py-4 text-sm text-muted-foreground">
          No subtasks yet.
        </div>
      )}

      {canEdit && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            value={newSubtaskName}
            onChange={(event) => setNewSubtaskName(event.target.value)}
            onKeyDown={handleSubtaskKeyDown}
            placeholder="Add a subtask"
            aria-label="New subtask name"
            className="h-9 flex-1"
          />
          <div className="flex items-center gap-2 sm:shrink-0">
            <DayPicker
              aria-label="New subtask due date"
              setDate={(date) => setNewSubtaskDueAt(toApiDate(date))}
              onClear={() => setNewSubtaskDueAt('')}
              date={toPickerDate(newSubtaskDueAt)}
              mode="button"
              isRequired={false}
            />
          </div>
          <Button
            type="button"
            size="sm"
            onClick={handleCreateSubtask}
            disabled={!trimmedSubtaskName || isCreatingSubtask}
          >
            Add
          </Button>
        </div>
      )}
    </div>
  );
};
